import React, { useState, useEffect } from "react";
import { Box, Divider, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import CustomTextField from "../../components/custom-text-field/CustomTextField";
import CustomDropdown from "../../components/custom-dropdown/CustomDropdown";
import CustomPointsRange from "../../components/custom-points-range/CustomPointsRange";
import CustomButton from "../custom-button/CustomButton";
import {
  BTN_CANCEL,
  BTN_SAVE_DRAFT,
  BTN_SAVE_MILESTONE,
  CODE_HELPER_TEXT,
  FORM_LABEL_MILESTONE_CODE,
  FORM_LABEL_MILESTONE_DESCRIPTION,
  FORM_LABEL_MILESTONE_NAME,
  FORM_LABEL_MILESTONE_STATUS,
  FORM_LABEL_MILESTONE_TYPE,
  FORM_LABEL_POINTS_RANGE,
  FORM_LABEL_TRIMESTER,
  FORM_PLACEHOLDER_MILESTONE_DESCRIPTION,
  FORM_PLACEHOLDER_MILESTONE_TYPE_DROPDOWN,
  FORM_PLACEHOLDER_MULTI_CODE,
  FORM_PLACEHOLDER_SINGLE_CODE,
  FORM_PLACEHOLDER_TEXTFIELD,
  FORM_PLACEHOLDER_TRIMESTER_DROPDOWN,
  MULTI_LANGUAGE_SUPPORT,
  MULTI_LANGUAGE_SUPPORT_NOTE,
} from "../../utility/constants/locale";
import {
  formatEducationMilestoneData,
  formatTaskTypes,
  getDropdownDataForCategory,
  getStatusIDFromStatusName,
  segregateCategoryWiseStatus,
} from "../../utility/functions/common-functions";
import { DisplayGroup } from "../../utility/interfaces/masterDataInterfaces";
import { useAppSelector } from "../../store/hooks";
import { useNavigate } from "react-router-dom";
import CustomGroupedRadioButton from "../custom-grouped-radio-btns/CustomGroupedRadioButton";
import {
  CATEGORY_ID_MAPPING,
  MILESTONE_MESSAGES,
  PATIENTTASK_STATUS_ID_MAPPING,
  REGEX_VALIDATIONS,
  TASK_MODE_RADIO_BTN_OPTIONS,
} from "../../utility/constants/common-constants";
import { enqueueSnackbar } from "notistack";
import LanguagePills from "../language-pills/LanguagePills";
import MultiLanguageForm from "../multi-language-form/MultiLanguageForm";
import ConfirmationModal from "../multi-language-form/ConfirmationModal";

interface MilestoneContentFormProps {
  initialValues: {
    isMulticode?: boolean;
    milestoneTypeId: number;
    displayGroupId: number;
    statusId: number;
    milestoneDisplayName: string;
    sourceMilestoneCode: string;
    description: string;
    rewardPoints: number;
    educationalContentTypeId: number;
  };
  onSubmit: (values: any) => void;
  onSaveDraft?: (values: any) => void;
  isEditMode?: boolean;
  isMilestoneInUse?: boolean;
  milestoneData: DisplayGroup[];
  translations?: {
    milestoneTranslationId: number;
    milestoneId: number;
    languageId: number;
    displayName: string;
    longDescription: string;
  }[];
  languageIds?: number[];
}

const MilestoneContentForm: React.FC<MilestoneContentFormProps> = ({
  initialValues,
  onSubmit,
  onSaveDraft,
  isEditMode = false,
  isMilestoneInUse = false,
  milestoneData,
  languageIds,
  translations,
}) => {
  const navigate = useNavigate();
  const [formValues, setFormValues] = useState(initialValues);
  const [multiLanguageValues, setMultiLanguageValues] = useState<{
    [key: number]: { name: string; description: string };
  }>({});
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedLanguageIds, setSelectedLanguageIds] = useState<number[]>([]);
  const categoryWiseStatus = useAppSelector(
    (state) => state.adminMasterData.categoryWiseStatus
  );
  const activeLanguages = useAppSelector(
    (state) => state.adminMasterData.activeLanguages
  );
  const taskTypes = useAppSelector((state) => state.adminMasterData.taskTypes);
  const draftStatusId = getStatusIDFromStatusName(
    CATEGORY_ID_MAPPING.patientTask,
    PATIENTTASK_STATUS_ID_MAPPING.draft,
    categoryWiseStatus
  );

  const validateForm = () => {
    const requiredFields: (keyof typeof formValues)[] = [
      "milestoneTypeId",
      "milestoneDisplayName",
      "sourceMilestoneCode",
      "rewardPoints",
      "displayGroupId",
    ];

    // Multi-code validation
    if (formValues.isMulticode) {
      const codes = formValues.sourceMilestoneCode
        .split(",")
        .map((code) => code.trim());
      const containsAtLeastOneComma =
        formValues.sourceMilestoneCode.includes(",");
      const hasAtLeastTwoCodes =
        codes.filter((code) => code.length > 0).length >= 2;

      if (!containsAtLeastOneComma || !hasAtLeastTwoCodes) {
        enqueueSnackbar(MILESTONE_MESSAGES.Validation.MultiCodeInvalid, {
          variant: "warning",
        });
        return false;
      }
    }

    // Required fields validation
    const areRequiredFieldsValid = requiredFields.every(
      (field) => !!formValues[field]
    );

    if (!areRequiredFieldsValid) {
      enqueueSnackbar(MILESTONE_MESSAGES.Validation.RequiredFields, {
        variant: "warning",
      });
    }

    return areRequiredFieldsValid;
  };

  useEffect(() => {
    setFormValues(initialValues);

    if (translations && translations.length > 0) {
      const mappedTranslations = translations.reduce((acc, translation) => {
        acc[translation.languageId] = {
          name: translation.displayName,
          description: translation.longDescription,
        };
        return acc;
      }, {} as { [key: number]: { name: string; description: string } });
      setMultiLanguageValues(mappedTranslations);
    }

    if (languageIds && languageIds.length > 0) {
      setSelectedLanguageIds(languageIds);
    }
  }, [initialValues, translations, languageIds]);

  const handleInputChange = (
    name: string,
    value: string | number | boolean
  ) => {
    if (name === "isMulticode") {
      if (typeof value === "boolean") {
        setFormValues({
          ...formValues,
          [name]: value,
          sourceMilestoneCode: "",
        });
      }
    } else {
      setFormValues({
        ...formValues,
        [name]: value,
      });
    }

    if (name === "milestoneDisplayName") {
      const languageId = activeLanguages.find(
        (lang) => lang.languageId === 1
      )?.languageId;

      if (languageId && value) {
        setSelectedLanguageIds((prevIds) =>
          prevIds.includes(languageId) ? prevIds : [...prevIds, languageId]
        );
      } else if (languageId && !value) {
        setSelectedLanguageIds((prevIds) =>
          prevIds.filter((id) => id !== languageId)
        );
      }
    }
  };

  const handleSubmit = () => {
    const translations = Object.entries(multiLanguageValues).map(
      ([languageId, values]) => ({
        languageId: Number(languageId),
        name: values.name,
        description: values.description,
      })
    );

    onSubmit({ ...formValues, translations });
    setModalOpen(false);
  };

  const handleSaveMilestoneClick = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (validateForm()) {
      setModalOpen(true);
    }
  };

  const handleSaveDraft = () => {
    const translations = Object.entries(multiLanguageValues).map(
      ([languageId, values]) => ({
        languageId: Number(languageId),
        name: values.name,
        description: values.description,
      })
    );
    if (validateForm()) {
      if (onSaveDraft) {
        onSaveDraft({ ...formValues, translations });
      }
    }
  };

  const handleCancel = () => {
    setFormValues(initialValues);
    navigate("/admin");
  };

  // To Do - Replace with actual data from Redux store
  const groupedOptions = formatEducationMilestoneData(milestoneData);
  const formattedTaskTypes = formatTaskTypes(taskTypes);

  const groupedCategoryStatus = segregateCategoryWiseStatus(categoryWiseStatus);

  const isDraft = isEditMode && initialValues.statusId === draftStatusId;
  const statusDropdownData = getDropdownDataForCategory(
    groupedCategoryStatus,
    CATEGORY_ID_MAPPING.patientTask,
    isDraft
  );

  const handleMultiLanguageChange = (
    languageId: number,
    values: { name: string; description: string }
  ) => {
    setMultiLanguageValues((prevValues) => {
      const updatedValues = { ...prevValues };
      if (!values.name.trim() && !values.description.trim()) {
        delete updatedValues[languageId];
      } else {
        updatedValues[languageId] = values;
      }
      return updatedValues;
    });

    if (languageId && values.name) {
      setSelectedLanguageIds((prevIds) =>
        prevIds.includes(languageId) ? prevIds : [...prevIds, languageId]
      );
    } else if (languageId && !values.name) {
      setSelectedLanguageIds((prevIds) =>
        prevIds.filter((id) => id !== languageId)
      );
    }
  };

  const onCloseModal = () => {
    setModalOpen(false);
  };

  return (
    <Box component="form" onSubmit={handleSaveMilestoneClick} mt={3} p={2}>
      <Grid container spacing={1} mb={2} alignItems={"center"}>
        <Grid size={12}>
          <LanguagePills
            activeLanguages={activeLanguages}
            selectedLanguageIds={selectedLanguageIds}
          />
        </Grid>
        <Grid size={3}>
          <CustomGroupedRadioButton
            disabled={isEditMode}
            options={TASK_MODE_RADIO_BTN_OPTIONS}
            changeTrigger={(value) => handleInputChange("isMulticode", value)}
            value={formValues.isMulticode!}
          />
        </Grid>
        <Grid size={5}>
          <CustomTextField
            isRequired
            maxLength={128}
            inputFormTitle={FORM_LABEL_MILESTONE_CODE}
            value={formValues.sourceMilestoneCode}
            changeTrigger={(value) =>
              handleInputChange("sourceMilestoneCode", value)
            }
            disabled={isEditMode}
            placeholder={
              formValues.isMulticode
                ? FORM_PLACEHOLDER_MULTI_CODE
                : FORM_PLACEHOLDER_SINGLE_CODE
            }
            validationRegex={
              formValues.isMulticode
                ? REGEX_VALIDATIONS.MULTI_CODE_REGEX
                : REGEX_VALIDATIONS.SINGLE_CODE_REGEX
            }
          />
        </Grid>
        {formValues.isMulticode && (
          <Grid size={3} mt={3}>
            <Typography variant="body2">{CODE_HELPER_TEXT}</Typography>
          </Grid>
        )}
      </Grid>
      <Grid container spacing={1} mb={2}>
        <Grid size={3}>
          <CustomDropdown
            inputFormTitle={FORM_LABEL_MILESTONE_TYPE}
            value={formValues.milestoneTypeId}
            changeTrigger={(value) =>
              handleInputChange("milestoneTypeId", value)
            }
            options={formattedTaskTypes}
            isRequired
            disabled={isEditMode}
            placeholder={FORM_PLACEHOLDER_MILESTONE_TYPE_DROPDOWN}
          />
        </Grid>
        <Grid size={5}>
          <CustomTextField
            inputFormTitle={FORM_LABEL_MILESTONE_NAME}
            value={formValues.milestoneDisplayName}
            changeTrigger={(value) =>
              handleInputChange("milestoneDisplayName", value)
            }
            isRequired={true}
            placeholder={FORM_PLACEHOLDER_TEXTFIELD}
            maxLength={64}
          />
        </Grid>
      </Grid>
      <Grid container spacing={1} mb={2}>
        <Grid size={3}>
          <CustomPointsRange
            min={0}
            max={150}
            step={25}
            value={formValues.rewardPoints}
            onChange={(value) => handleInputChange("rewardPoints", value)}
            label={FORM_LABEL_POINTS_RANGE}
            isRequired
          />
        </Grid>
        <Grid size={3}>
          <CustomDropdown
            inputFormTitle={FORM_LABEL_TRIMESTER}
            value={formValues.displayGroupId}
            changeTrigger={(value) =>
              handleInputChange("displayGroupId", value)
            }
            options={[]}
            isRequired
            groupedOptions={groupedOptions}
            disabled={isMilestoneInUse}
            placeholder={FORM_PLACEHOLDER_TRIMESTER_DROPDOWN}
          />
        </Grid>
        {isEditMode && (
          <Grid size={3}>
            <CustomDropdown
              inputFormTitle={FORM_LABEL_MILESTONE_STATUS}
              value={formValues.statusId}
              changeTrigger={(value) => handleInputChange("statusId", value)}
              options={statusDropdownData}
            />
          </Grid>
        )}
      </Grid>
      <Grid container spacing={1} mb={2}>
        <Grid size={11}>
          <CustomTextField
            inputFormTitle={FORM_LABEL_MILESTONE_DESCRIPTION}
            value={formValues.description}
            changeTrigger={(value) => handleInputChange("description", value)}
            multi
            rows={4}
            maxLength={512}
            isCharacterCount
            placeholder={FORM_PLACEHOLDER_MILESTONE_DESCRIPTION}
          />
        </Grid>
      </Grid>
      <Grid container spacing={1} mb={3}>
        <Grid>
          <Typography
            variant="h3"
            sx={{ fontWeight: "500", lineHeight: "18.75px" }}
          >
            {MULTI_LANGUAGE_SUPPORT}
          </Typography>
        </Grid>
        <Grid>
          <Typography variant="body2" sx={{ color: "#F40009" }}>
            {MULTI_LANGUAGE_SUPPORT_NOTE}
          </Typography>
        </Grid>
      </Grid>
      {activeLanguages
        .filter((language) => language.languageId !== 1)
        .map((language, index) => (
          <>
            <MultiLanguageForm
              key={language.languageId}
              language={language.languageName}
              name={multiLanguageValues[language.languageId]?.name || ""}
              description={
                multiLanguageValues[language.languageId]?.description || ""
              }
              onChange={(values) =>
                handleMultiLanguageChange(language.languageId, values)
              }
            />
            {index < activeLanguages.length - 2 && (
              <Divider
                sx={{ borderStyle: "dashed", borderWidth: "1px", my: 2 }}
              />
            )}
          </>
        ))}
      <Box display="flex" justifyContent="flex-start" gap={2}>
        <CustomButton
          variant="outlined"
          onClick={handleCancel}
          text={BTN_CANCEL}
        />
        <>
          <CustomButton
            variant="outlined"
            onClick={handleSaveDraft}
            text={BTN_SAVE_DRAFT}
            disabled={isEditMode && formValues.statusId !== draftStatusId}
          />
          <CustomButton
            type="submit"
            variant="contained"
            color="primary"
            text={BTN_SAVE_MILESTONE}
            disabled={isEditMode && formValues.statusId === draftStatusId}
          />
        </>
      </Box>
      <ConfirmationModal
        modalOpen={modalOpen}
        onCloseModal={onCloseModal}
        handleSave={handleSubmit}
      />
    </Box>
  );
};

export default MilestoneContentForm;
